import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useSnackbar } from 'notistack';
import { includes, prop } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Form from '@gef-ui/form-material/Form';
import { getLabelMessage } from '@gef-ui/localization/messages';
import Print from '@gef-ui/material-icons/Print';

import { useDefaultRedirect } from 'app/hooks';
import { Dialog } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import ProspectorButton from 'components/atoms/GefMui/ProspectorButton';
import Subheader from 'components/atoms/GefMui/Subheader';
import GOLPContactLink from 'components/atoms/PKUrl/GOLPContactLink';
import GREQContact133Link from 'components/atoms/PKUrl/GREQContact133Link';
import MODALS from 'constants/modals';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import contactDetail from 'features/views/contactDetail';
import { setCameToDetailFrom } from 'features/views/contactDetail/actions';
import { CONTACT_NOT_FOUND_ERR_MSG } from 'features/views/contactDetail/constants';
import formDescriptorFn, { CONTACT_DETAIL_FORM } from 'features/views/contactDetail/form';
import contactFeedback from 'features/views/contactFeedback';
import contacts from 'features/views/contacts';
import user from 'features/views/user';
import { getRoutePath } from 'utils';
import BruttoProductionModal from 'pages/BruttoProductionModal';

import AdditionalData from './components/AdditionalData';
import AuthorizedPersons from './components/AuthorizedPersons';
import Feedback from './components/Feedback';
import HistoryTable from './components/HistoryTable';
import Notes from './components/Notes';

const fetchStorePath = `${fetch.constants.urls.contactDetail}.get`;

export const ContactDetail = ({ contactId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();

	const userRole = useSelector(user.selectors.getCurrentRole);
	const contact = useSelector(contactDetail.selectors.getDetail);
	const {
		additionalData = [],
		history: contactHistory = [],
		comments = [],
		campaign = {},
		canRedirectToGreq,
		client,
	} = contact;

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: `${getLabelMessage('contactDetail:header')} (${campaign.fullName || contactId})`,
		backButton: true,
	});

	const { state } = useLocation();
	const defaultRedirectIfFetchFails = useDefaultRedirect();
	const { status: errorStatus = null, parsedContent = {} } = useSelector(fetch.selectors.getMetaData(fetchStorePath));
	const cameToDetailFrom = prop('cameToDetailFrom', contact);

	useEffect(() => {
		if (!isNilOrEmpty(state?.from)) {
			dispatch(setCameToDetailFrom(state?.from));
		}
	}, [dispatch, state]);

	useEffect(() => {
		if (errorStatus === 404 && includes(CONTACT_NOT_FOUND_ERR_MSG, parsedContent?.message)) {
			// direct url to detail (from GPK etc)
			if (isNilOrEmpty(cameToDetailFrom)) {
				navigate(defaultRedirectIfFetchFails);
			} else {
				if (cameToDetailFrom === '/contacts') {
					dispatch(contacts.actions.reload('list'));
				}
				// autoreload on page after navigate - campaignManagement, campaignDashboards
				navigate(-1);
			}
			enqueueSnackbar(getLabelMessage('toastr:contactDetailErrorUserNotResolver'), { variant: 'error' });
		}
	}, [
		dispatch,
		navigate,
		enqueueSnackbar,
		errorStatus,
		parsedContent?.message,
		defaultRedirectIfFetchFails,
		cameToDetailFrom,
	]);

	useEffect(() => {
		dispatch(contactDetail.actions.getContactDetail(contactId));
		dispatch(contactDetail.actions.fetchContactAdditionalData(contactId));
	}, [dispatch, contactId]);

	useEffect(
		() => () => {
			dispatch(contactDetail.actions.resetContactDetail());
		},
		[dispatch],
	);

	const handleFeedbackSuccess = useCallback(
		(id) => {
			dispatch(user.actions.fetchAdditionalUserInfo());
			dispatch(contacts.actions.replaceContact(id));
			dispatch(contactDetail.actions.getContactDetail(id));
			dispatch(contactDetail.actions.fetchContactAdditionalData(id));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(contactDetail.actions.getContactDetail(contactId));
		dispatch(contactDetail.actions.fetchContactAdditionalData(contactId));
	}, [dispatch, contactId]);

	const handlePrintButton = useCallback(() => {
		const { pathname, state } = getRoutePath('printContactDetail', { contactId }, true);
		navigate(pathname, { state });
	}, [navigate, contactId]);

	const subheaderToolbar = (
		<Stack
			direction="row"
			spacing={1}
			sx={{
				justifyContent: 'flex-end',
			}}
		>
			<ProspectorButton businessIn={client?.businessIn} />
			{contact.campaign && contact.campaign.golp && (
				<GOLPContactLink contactId={contactId} userRole={userRole}>
					<Button variant="text" size="small" onClick={null}>
						{getLabelMessage('detail:asideBarButtonGOLP')}
					</Button>
				</GOLPContactLink>
			)}
			{contact.campaign && contact.campaign.greq && (
				<GREQContact133Link contactId={contactId} userRole={userRole}>
					<Button variant="text" size="small" onClick={null}>
						{getLabelMessage('detail:asideBarButtonGreq133')}
					</Button>
				</GREQContact133Link>
			)}
			<Button variant="text" size="small" startIcon={<Print />} onClick={handlePrintButton}>
				{getLabelMessage('common:print')}
			</Button>
		</Stack>
	);

	const floatingDialogs = (
		<>
			<Dialog name={contactFeedback.constants.dialogs.PARTNER_LOCKED_DIALOG} mui>
				{getLabelMessage('contactDetail:partnerLocked')}
			</Dialog>
			<Dialog name={contactFeedback.constants.dialogs.PARTNER_NOT_FOUND_IN_CCD} mui>
				{getLabelMessage('contactDetail:partnerNotFoundInCCD')}
			</Dialog>
		</>
	);

	return (
		<Panel
			screenId={PAGES.contactDetail.id}
			screenName={PAGES.contactDetail.name}
			floatingDialogs={floatingDialogs}
		>
			<BruttoProductionModal id={MODALS.brutto_production} />
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<Subheader title={subheaderTitle} headerToolbar={subheaderToolbar} />
					<Feedback
						contact={contact}
						handleFeedbackSuccess={handleFeedbackSuccess}
						fetchStorePath={fetchStorePath}
					/>
				</Stack>
			</Paper>
			{canRedirectToGreq && <AuthorizedPersons contact={contact} />}
			<AdditionalData
				additionalData={additionalData}
				handleReload={handleReload}
				fetchStorePath={fetchStorePath}
			/>
			<Form name={CONTACT_DETAIL_FORM} formDescriptorFn={formDescriptorFn}>
				<Notes contactId={contactId} comments={comments} />
			</Form>
			<HistoryTable history={contactHistory} handleReload={handleReload} fetchStorePath={fetchStorePath} />
		</Panel>
	);
};

ContactDetail.propTypes = {
	contactId: PropTypes.string,
};

export default ContactDetail;
